import { LitElement, html, property } from 'lit-element'
import { ifDefined } from 'lit-html/directives/if-defined'
import { register } from '@polleverywhere/hmr-custom-element'
import { bemBlock } from '@polleverywhere/bem-helpers'
import { defineElementSlot } from '@polleverywhere/element-helpers'
import { contextualize, cutlassEvent } from '@polleverywhere/cutlass'
import { snakeCase } from 'snake-case'
import '@polleverywhere/icon-element'
import './pe-menu-item.scss'

/**
 * Menu item is what a menu is composed of and is selectable. Menu items may
 * exist in a disabled state. Otherwise, each selection of a menu item is
 * considered an "action" and is emitted as an event.
 *
 * @element pe-menu-item
 *
 * @slot - Menu item label
 *
 * @prop {boolean} disabled True to render a disabled menu item. Reflected to an
 *  attribute that you can use to style this state accordingly in CSS.
 * @prop {string} icon Optional icon glyph name to render next to the label
 * @prop {string} name Optional unique identifier for the menu item, which is
 *  included when emitting an "action" event
 *
 * @fires {Object} action - Fires when menu item is selected. The menu item's
 *  "name" is attached to the event detail. If "name" is not defined, the label
 *  is attached instead.
 *
 * @cssprop --pe-menu-item-color=$pe-neutral-900 - Text color
 * @cssprop --pe-menu-item-icon-color=$pe-blue-500 - Icon color
 */
export default class MenuItem extends LitElement {
  static tagName = 'pe-menu-item'

  @property({ type: Boolean, reflect: true }) disabled = false

  @property({ type: String }) icon

  @property({ type: String }) name

  constructor () {
    super()

    // contextualize cutlass events
    contextualize(this, this.tagName)
  }

  connectedCallback () {
    super.connectedCallback()
    defineElementSlot(this)
  }

  createRenderRoot () { return this }

  render () {
    const label = this.slots().map(node => node.textContent.trim()).join('')

    // Store `data-original-label` attribute to preserve the original label
    // since Chrome Translate may modify the values in the DOM.
    return html`<button
      @click=${handleAction}
      aria-label=${label}
      class=${bem('button')}
      data-original-label=${label}
      ?disabled=${this.disabled}
      name=${ifDefined(this.name)}
      role='menuitem'>
      ${this.icon && html`<icon-element
        class=${bem('icon')}
        name=${this.icon}
        type='glyph'
      ></icon-element>`}
      <span>${this.slots()}</span>
    </button>`
  }
}

function handleAction (e) {
  e.stopPropagation()

  const target = e.currentTarget
  const name = target.name || target.dataset.originalLabel

  // Dispatch an event to Cutlass. This is emitted before the item's action
  // event because some downstream consumers could remove this element from the
  // DOM. If the Cutlass event is sent after this element is removed from the
  // DOM, it will not ascend the DOM tree to the root element and be sent to
  // Cutlass.
  cutlassEvent(this, `action:${snakeCase(name).toLowerCase()}`)

  this.dispatchEvent(new CustomEvent('action', {
    bubbles: true,
    detail: {
      name
    }
  }))
}

const bem = bemBlock(MenuItem.tagName)
register(module, MenuItem.tagName, MenuItem)
